import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  ArrowRight,
  Users,
  Globe,
  Heart,
  Target,
  Calendar,
  MapPin,
  TrendingUp,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Play,
  Share2,
  BookOpen,
  MessageCircle,
  Star,
  CheckCircle,
  Clock,
  User,
  Building2,
  Handshake,
  Home,
  Award,
  Shield,
  Coffee,
  Camera,
  Mic
} from "lucide-react";
import HeroImage from "../../assets/images/journey/674d99c9318ae90917879819.jpg";

// Counter Animation Hook
const useCountAnimation = (end, duration = 2, startWhen = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startWhen]);

  return count;
};

const AnimatedCounter = ({ end, suffix = "" }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const count = useCountAnimation(end, 2, inView);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-200 rounded-full"
              >
                <Users className="w-4 h-4 text-blue-600 mr-2" />
                <span className="text-blue-700 text-sm font-semibold">Community Initiative</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Back</span>
                  <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">2</span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">Community</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                Empowering communities through collaborative initiatives, fostering local engagement, and building stronger social connections in our increasingly digital world.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-6 sm:gap-8 pt-4"
              >
                <div>
                  <div className="text-2xl font-bold text-blue-600">Community</div>
                  <div className="text-sm text-gray-600 font-medium">Building</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">Social</div>
                  <div className="text-sm text-gray-600 font-medium">Connection</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">Local</div>
                  <div className="text-sm text-gray-600 font-medium">Impact</div>
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-blue-600 rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={HeroImage}
                alt="Back2Community - Community gathering and collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-blue-600">Mission</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Rebuilding the fabric of community life through meaningful connections, local initiatives, and collaborative action
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Mission Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Community Revival</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Revitalizing local communities by encouraging active participation, volunteerism, and collaborative problem-solving at the grassroots level.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Social Connection</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Creating platforms and opportunities for meaningful interactions that transcend digital barriers and foster real-world relationships.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Local Leadership</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Identifying and nurturing local leaders who can drive positive change from within their communities.
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-blue-50 rounded-2xl p-6 border border-blue-200"
            >
              <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Vision Statement
              </h4>
              <p className="text-blue-700 italic">
                "To create thriving communities where every individual feels valued, connected, and empowered to contribute to collective well-being and shared prosperity."
              </p>
            </motion.div>
          </motion.div>

          {/* Right - Mission Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={HeroImage}
                alt="Community volunteering and collaboration"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              

            </div>

            {/* Floating Element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center shadow-xl"
            >
              <Users className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const impactStats = [
    { icon: Users, label: "Volunteer Network", text: "Growing" },
    { icon: Building2, label: "Community Reach", text: "Expanding" },
    { icon: Calendar, label: "Local Events", text: "Regular" },
    { icon: Handshake, label: "Partnerships", text: "Strong" },
    { icon: Heart, label: "Social Impact", text: "Meaningful" },
    { icon: Star, label: "Success Stories", text: "Inspiring" }
  ];

  const initiatives = [
    {
      title: "Neighborhood Connect",
      description: "Regular meetups and social events to strengthen local bonds",
      image: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=400&h=300&auto=format&fit=crop",
      impact: "Active Initiative"
    },
    {
      title: "Community Gardens",
      description: "Collaborative green spaces that bring people together",
      image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=400&h=300&auto=format&fit=crop",
      impact: "Green Spaces"
    },
    {
      title: "Local Skills Exchange",
      description: "Platform for community members to share and learn skills",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=400&h=300&auto=format&fit=crop",
      impact: "Skill Sharing"
    }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Initiative <span className="text-blue-600">Impact</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Building stronger communities through meaningful connections and collaborative initiatives
          </p>
        </motion.div>

        {/* Impact Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-20">
          {impactStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-200/50"
            >
              <div className="flex items-center justify-between mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-3 h-3 bg-blue-400 rounded-full"
                />
              </div>
              
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {stat.text}
              </div>
              <p className="text-gray-700 font-semibold">{stat.label}</p>
            </motion.div>
          ))}
        </div>
        </div>
    </section>
  );
};

// Main Back2Community Component
const Back2CommunityInitiative = () => {
  useEffect(() => {
    document.title = "Back2Community Initiative - Harsh Sanghani";
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MissionSection />
      <ImpactSection />
    </div>
  );
};

export default Back2CommunityInitiative;